import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./login.css";
import { TextField, Icon, PrimaryButton, mergeStyles } from "@fluentui/react";
import {
  useGoogleLogin,
} from "@react-oauth/google";
import { LoginSchema } from "../../Schemas/LoginSchema";
import { useFormik } from "formik";
import { encryptData } from "../../helpers/RSAUtility";
import { useNavigate } from "react-router-dom";
import userContext from "../../context/users/UserContext";
import {
  API_BASE_URL,
  AuthRoutes,
  LOGIN_FAILED_REASON,
  NonAuthRoutes, resolveBackendForHost
} from "../../constants/Constants";
import ATextField from "../../components/ATextField";
import Google from "../../components/SVGComponents/Logo/Google";
import { authenticateGoogleUser, getUserInfo } from "../../service/UserService";
import { t } from "i18next";
import VDCLogo from "../../components/SVGComponents/Logo/VDCLogo";
import VDC_AIPoweredCloudPacs from "../../components/SVGComponents/Logo/VDC_AIPoweredCloudPacs";
import { getEnv } from '../../helpers/EnvHelper';
import { markOtpSent, OTP_FLOW } from "../../helpers/OtpCooldown";
import { isPasswordResetPending } from "../../helpers/PasswordResetFlags";

const Login = () => {
  const { signin, loadByUsername, authenticated } = useContext(userContext);
  const { matched: hideBranding, loginLogoWidth, loginLogoHeight, loginLogoTop, loginLogoLeft } = resolveBackendForHost(window.location.host);
  const loginLogoStyle = {
    width: loginLogoWidth || "109px",
    height: loginLogoHeight || "24px",
    top: loginLogoTop || "22px",
    left: loginLogoLeft || "15px",
  };
  
  const credentials = {
    username: "",
    password: "",
  };

  let navigate = useNavigate();



  const [loginFailed, setLoginFailed] = useState(false);
  const [loginFailedReason, setLoginFailedReason] = useState("");
  const [rateLimitMessage, setRateLimitMessage] = useState("");
  // A refusal that is not about the password (concurrent-session limit, ...).
  // Shown verbatim, without the "invalid credentials" / attempts-remaining wording.
  const [loginErrorMessage, setLoginErrorMessage] = useState("");
  const [googleLoginFailed, setGoogleLoginFailed] = useState(false);
  const [googleLoginFailedReason, setGoogleLoginFailedReason] = useState("");
  const [domainLogo, setDomainLogo] = useState(null);
  const [domainLogo2, setDomainLogo2] = useState(null);
  const [loginDisabled, setLoginDisabled] = useState(false);
  const [networkError, setNetworkError] = useState(false);
  const loginSafetyTimer = useRef(null);


  useEffect(() => {
    return () => clearTimeout(loginSafetyTimer.current);
  }, []);

  useEffect(() => {
    // console.log("Authenticated- "+authenticated);
    if (authenticated) {
      // OLD: navigate(AuthRoutes.STUDYLIST);
      // NEW: this effect runs right after the login's own redirect to Change Password and used to
      // override it, so a user with a pending password reset landed on the study list instead.
      navigate(isPasswordResetPending() ? AuthRoutes.FORCECHANGEPWD : AuthRoutes.STUDYLIST);
    }
  }, [authenticated])

  useEffect(() => {
    const fetchDomainLogo = async () => {
      try {
        const host = window.location.hostname;

        const res = await fetch(
          `${API_BASE_URL}rebranding/login-logo?host=${encodeURIComponent(host)}`,
          { method: "GET" }
        );

        if (res.status === 200) {
          const blob = await res.blob();
          setDomainLogo(URL.createObjectURL(blob));
        } else {
          console.info("No logo found, using default");
        }

        try {
          const res2 = await fetch(
            `${API_BASE_URL}rebranding/login-logo-2?host=${encodeURIComponent(host)}`,
            { method: "GET" }
          );
          if (res2.status === 200) {
            const blob2 = await res2.blob();
            setDomainLogo2(URL.createObjectURL(blob2));
          }
        } catch (err2) {
          console.error("[Branding] Failed to load logo-2", err2);
        }
      } catch (err) {
        console.error("[Branding] Failed to load logo", err);
      }
    };

    // 🔒 KEEP VDC LOGIC UNTOUCHED
    if (getEnv("REACT_APP_VDC_PLATFORM") === "false") {
      fetchDomainLogo();
    }
  }, []);


  const { values, errors, touched, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues: credentials,
      validateOnBlur: false,
      validationSchema: () => LoginSchema(t),
      onSubmit: async (values, action) => {
        setLoginDisabled(true);
        loginSafetyTimer.current = setTimeout(() => setLoginDisabled(false), 10000);

        const encryptedUsername = encryptData(values.username);
        const encryptedPassword = encryptData(values.password);

        const response = await signin(encryptedUsername, encryptedPassword);

        if (!response || response.status === 0) {
          clearTimeout(loginSafetyTimer.current);
          setLoginDisabled(false);
          setNetworkError(true);
          setLoginFailed(false);
          setLoginErrorMessage("");
          action.resetForm();
          return;
        }
        setNetworkError(false);

        // OLD: const isForceReset =
        // OLD:   localStorage.getItem("forceReset") === "true" ||
        // OLD:     localStorage.getItem("redirectOnLogin") === "true"
        // OLD:     ? true
        // OLD:     : false;
        // NEW (VAPT): signin() has just put this login's flags in memory.
        const isForceReset = isPasswordResetPending();

        if (response.status === 200) {
          setLoginFailed(false);
          setLoginErrorMessage("");
          // OLD: if (localStorage.getItem("showMaintenance")) {
          if (sessionStorage.getItem("showMaintenance")) {
            // OLD: localStorage.removeItem("showMaintenance");
            sessionStorage.removeItem("showMaintenance");
          }
          if (isForceReset) {
            // OLD: navigate(AuthRoutes.FORCECHANGEPWD);
            // NEW: full page load. An in-app navigation kept the login page's light ThemeProvider
            // around long enough to strip the app's body theme class on unmount (same class for a
            // light-theme user), leaving Change Password with no background until a refresh.
            window.location.assign(AuthRoutes.FORCECHANGEPWD);
          } else {
            // if(response.data.result === "Pending" && response.data.message.includes("2FA")){
            //   sessionStorage.setItem("2faUsername", values.username);
            //   sessionStorage.setItem("2faEmail", response.data.data.email);
            //   navigate(NonAuthRoutes.TWOFACTORAUTHENTICATION);
            // }
            if (response.data.result === "Pending" && response.data.message.includes("2FA")) {
              sessionStorage.setItem("2faUsername", values.username);

              const dto = (response.data && response.data.data) ? response.data.data : {};

              // Save the masked email (if backend returned it)
              if (dto.email) {
                sessionStorage.setItem("2faEmail", dto.email);
              }

              // Save the masked whatsapp number (if backend returned it)
              if (dto.whatsappNumber) {
                sessionStorage.setItem("2faWhatsApp", dto.whatsappNumber);
              }

              // Save channel flags (optional — useful for UI)
              if (typeof dto.emailSent !== "undefined") {
                sessionStorage.setItem("2faEmailSent", dto.emailSent.toString());
              }
              if (typeof dto.whatsappSent !== "undefined") {
                sessionStorage.setItem("2faWhatsappSent", dto.whatsappSent.toString());
              }

              // Stamp the send so the 2FA screen's resend countdown runs from here and
              // survives a refresh, instead of restarting whenever that page mounts.
              markOtpSent(OTP_FLOW.TWO_FA);

              navigate(NonAuthRoutes.TWOFACTORAUTHENTICATION);
            }

            else{
              // OLD: localStorage.setItem("advViewerSlots", JSON.stringify(Array(10).fill({ available: 0, uuid: null })));
              // (Slots are browser Web Locks now and free themselves when a viewer tab closes, so
              // there is nothing to reset at login - see StudyViewerState getSlotAdvanceViewerUrl.)
              navigate(AuthRoutes.STUDYLIST);
            }
          }
        } else {
          clearTimeout(loginSafetyTimer.current);
          setLoginDisabled(false);
          console.log(response);
          setNetworkError(false);
          // For a wrong password the backend sends a reason code or a remaining-attempts count,
          // which the attempts wording below is built around. Anything else is a full sentence.
          const reason = response.data?.message;
          const isPasswordFailure =
            Object.values(LOGIN_FAILED_REASON).includes(reason) || /^\d+$/.test(String(reason));
          // 429 is the OTP throttle, raised once the credentials are already valid - it is
          // not a wrong-password response, so it must not be shown as an attempt count.
          if (response.status === 429) {
            console.warn("[Login] OTP request throttled by server", reason);
            setLoginErrorMessage("");
            setLoginFailed(true);
            setRateLimitMessage(
              reason || "Too many verification code requests. Please try again later."
            );
            setLoginFailedReason(LOGIN_FAILED_REASON.RATE_LIMITED);
          } else if (isPasswordFailure) {
            setLoginErrorMessage("");
            setLoginFailed(true);
            setLoginFailedReason(reason);
          } else {
            setLoginFailed(false);
            setLoginErrorMessage(reason || t("login.invalid-credentials"));
          }
        }

        action.resetForm();
      },
    });

  const loginWithGoogle = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      const userInfo = await getUserInfo(access_token);
      console.log(userInfo);

      if (userInfo.email != null) {
        const response = await authenticateGoogleUser(userInfo.email);
        if (response.status === 200) {
          navigate(AuthRoutes.STUDYLIST);
        } else {
          setGoogleLoginFailed(true);
          setGoogleLoginFailedReason(response.data.message);
        }
      }
    },
  });

  const handleKeyPress = (e) => {
    if (e.which === 13 && !loginDisabled) {
      handleSubmit();
    }
  };

  const customInputClass = mergeStyles({
    selectors: {
      "input:-webkit-autofill": {
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "#000000",
        transition: "#ffffff 5000s ease-in-out 0s",
        boxShadow: "inset 0 0 20px 20px #ffffff",
      },
      "input:-webkit-autofill:hover": {
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "#000000",
        transition: "#ffffff 5000s ease-in-out 0s",
        boxShadow: "inset 0 0 20px 20px #ffffff",
      },
      "input:-webkit-autofill:focus": {
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "#000000",
        transition: "#ffffff 5000s ease-in-out 0s",
        boxShadow: "inset 0 0 20px 20px #ffffff",
      },
      "input:-webkit-autofill:active": {
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "#000000",
        transition: "#ffffff 5000s ease-in-out 0s",
        boxShadow: "inset 0 0 20px 20px #ffffff",
      },
    },
  });

  return (
    <div
      className="ms-Grid container"
      dir="ltr"
      style={{
        height: "100%",
        width: "100%",
        padding: "0px",
        overflowY: "hidden",
        backgroundColor: "white",
      }}
      onKeyDown={handleKeyPress}
    >
      <div className="ms-Grid-row" style={{ width: "100%", height: "100vh" }}>
        <div
          className="ms-Grid-col ms-xl6 ms-hiddenLgDown"
          style={{ padding: "0", height: "100%" }}
        >
          {getEnv("REACT_APP_VDC_PLATFORM") === "true" ? (
            <VDC_AIPoweredCloudPacs />
          ) : (
            <img
              className="ai-powered-cloud-image"
              src={`${API_BASE_URL}rebranding/login-image`}
              alt="AI Powered Cloud Pacs"
              onError={(e) => {
                // hide image completely
                e.target.style.display = "none";
              }}
            />
          )}
        </div>
        <div
          className="ms-Grid-col ms-sm12 ms-md10 ms-lg8 ms-xl4 ms-xlPush1 ms-lgPush2 ms-mdPush1"
          style={{ padding: "0" }}
        >
          <div className="login-form-container">
            <form>
              {getEnv("REACT_APP_VDC_PLATFORM") === "true" && (
                <div
                  style={{
                    textAlign: "center",
                    width: "100%",
                    backgroundColor: "white",
                  }}
                >
                  <VDCLogo />
                </div>
              )}
              {getEnv("REACT_APP_VDC_PLATFORM") === "false" && domainLogo && (
                <img
                  src={domainLogo}
                  alt="Tenant Logo"
                  className="logo-mobile"
                  style={{ width: loginLogoWidth || "109px", height: loginLogoHeight || "24px" }}
                />
              )}
              {getEnv("REACT_APP_VDC_PLATFORM") === "false" && (
                <p
                  className="ms-fontSize-28 ms-fontWeight-semibold"
                  style={{ marginBottom: "0" }}
                >
                  {t("heading.login")}
                </p>
              )}
              <div>
                {getEnv("REACT_APP_VDC_PLATFORM") === "false" &&
                  getEnv("REACT_APP_ENABLE_LOGIN_WITH_GOOGLE") === "true" &&
                  getEnv("REACT_APP_ENTERPRISE_PLATFORM") === "false" && (
                    <div className="shadow-2xl">
                      {/* <GoogleLogin
                        clientId={
                          "732815663190-v9tunn3bgvfqqea0jckhjjgof9c5obhh.apps.googleusercontent.com"
                        }
                        render={(renderProps) => ( */}
                      <div
                        className="google-login cursor-pointer"
                        onClick={() => loginWithGoogle()}
                      >
                        <div className="google-btn">
                          <div className="google-icon-wrapper">
                            <Google />
                          </div>
                          <p className="btn-text">
                            <b>Login with Google</b>
                          </p>
                        </div>
                      </div>
                      {/* )}
                        onSuccess={responseMessage}
                        onFailure={responseMessage}
                      /> */}
                    </div>
                  )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                    margin: "20px 0 15px",
                    color: "#ff0000",
                  }}
                >
                  {networkError && (
                    <div
                      className="ms-fontSize-1"
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <Icon
                        iconName="InfoSolid"
                        className="ms-fontSize-14"
                        style={{ marginRight: "5px" }}
                      />
                      <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight-bold">
                        {t("login.server-unreachable")}
                      </p>
                    </div>
                  )}
                  {getEnv("REACT_APP_VDC_PLATFORM") === "false" &&
                    googleLoginFailed && (
                      <div
                        className="ms-fontSize-1"
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Icon
                          iconName="InfoSolid"
                          className="ms-fontSize-14"
                          style={{ marginRight: "5px" }}
                        ></Icon>
                        <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight-bold">
                          {googleLoginFailedReason}
                        </p>
                      </div>
                    )}
                  {loginErrorMessage && (
                    <div
                      className="ms-fontSize-1"
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon
                        iconName="InfoSolid"
                        className="ms-fontSize-14"
                        style={{ marginRight: "5px" }}
                      ></Icon>
                      <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight-bold">
                        {loginErrorMessage}
                      </p>
                    </div>
                  )}
                  {/* Not shown for a throttle: the credentials were accepted, only the OTP
                      request was rate limited, so "Invalid Username or Password" is wrong. */}
                  {loginFailed && loginFailedReason !== LOGIN_FAILED_REASON.RATE_LIMITED && (
                    <div
                      className="ms-fontSize-1"
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Icon
                        iconName="InfoSolid"
                        className="ms-fontSize-14"
                        style={{ marginRight: "5px" }}
                      ></Icon>
                      <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight-bold">
                        {t("login.invalid-credentials")}
                      </p>
                    </div>
                  )}

                  {loginFailed && loginFailedReason !== "INVALID" && (
                    <>
                      {loginFailedReason === LOGIN_FAILED_REASON.RATE_LIMITED ? (
                        <>
                          <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight">
                            {rateLimitMessage}
                          </p>
                        </>
                      ) : loginFailedReason === LOGIN_FAILED_REASON.DEACTIVATED ? (
                        <>
                          <p
                            className="ms-fontSize-12 login-form-error-messages ms-fontWeight"
                            style={{ marginBottom: "0px" }}
                          >
                            The account has been{" "}
                            <span className="ms-fontWeight-bold">
                              deactivated!
                            </span>
                          </p>
                          <p
                            className="ms-fontSize-12 login-form-error-messages ms-fontWeight"
                            style={{ marginTop: "0px" }}
                          >
                            {t("login.contact-administrator")}
                          </p>
                        </>
                      ) : loginFailedReason === LOGIN_FAILED_REASON.LOCKED ? (
                        <>
                          <p
                            className="ms-fontSize-12 login-form-error-messages ms-fontWeight"
                            style={{ marginBottom: "0px" }}
                          >
                            The account has been{" "}
                            <span className="ms-fontWeight-bold">locked!</span>
                          </p>
                          <p
                            className="ms-fontSize-12 login-form-error-messages ms-fontWeight"
                            style={{ marginTop: "0px" }}
                          >
                            {t("login.click-forgot-password")}
                          </p>
                        </>
                      ) : loginFailedReason ===
                        LOGIN_FAILED_REASON.ONE_ATTEMPT ? (
                        <>
                          <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight">
                            You have{" "}
                            <span className="ms-fontWeight-bold">
                              1 more attempt
                            </span>{" "}
                            remaining. The account will be locked after that if
                            you fail to login with valid credentials.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="ms-fontSize-12 login-form-error-messages ms-fontWeight">
                            You have{" "}
                            <span className="ms-fontWeight-bold">
                              {loginFailedReason} more attempts
                            </span>{" "}
                            remaining. The account will be locked after that if
                            you fail to login with valid credentials.
                          </p>
                        </>
                      )}
                    </>
                  )}
                </div>
                <ATextField
                  name="username"
                  label="Username"
                  onChange={handleChange}
                  value={values.username}
                  onBlur={handleBlur}
                  errorMessage={touched.username && errors.username}
                  className={customInputClass}
                />
              </div>
              <div style={{ marginTop: "0px" }}>
                <TextField
                  name="password"
                  type="password"
                  label="Password"
                  autoComplete="off"
                  canRevealPassword
                  onChange={handleChange}
                  value={values.password}
                  onBlur={handleBlur}
                  errorMessage={touched.password && errors.password}
                  className={customInputClass}
                />
              </div>
              <div onClick={() => navigate(NonAuthRoutes.FORGOTPASSWORD)}>
                <p
                  className="ms-fontSize-12 cursor-pointer"
                  style={{
                    textDecoration: "underline",
                    float: "right",
                    marginTop: "5px",
                  }}
                >
                  {t("login.forgot-password")}
                </p>
              </div>
              <div style={{ marginTop: "60px" }}>
                <PrimaryButton
                  className="ms-fontSize-14"
                  text="Login"
                  style={{ width: "175px" }}
                  onClick={handleSubmit}
                  disabled={loginDisabled}
                />
              </div>

              <div
                style={{
                  marginTop: "60px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >


                {domainLogo2 && (
                  <img
                    src={domainLogo2}
                    alt="Tenant Logo 2"
                    className="ms-hiddenLgUp"
                    style={{ height: "55px" }}
                  />
                )}

                <img
                  src={`${window.location.origin}/images/fda-logo.png`}
                  alt="fda-logo"
                  // width={100}
                />


                <img
                  src={`${window.location.origin}/images/ce-logo-blue.png`}
                  alt="ce-logo"
                  // width={100}
                />

                {/* <img
                    src={`${window.location.origin}/images/ce-logo.png`}
                    alt="fda-logo"
                    width={70}
                    style={{
                      marginLeft: "40px"
                    }}
                  /> */}
                {(!hideBranding &&
                <span
                  className="ms-fontColor-themePrimary cursor-pointer"
                  style={{
                    textDecoration: "underline",
                    fontSize: "14px",
                    letterSpacing: "0.5px",
                  }}
                  onClick={() => window.open("/privacy-policy", "_blank")}
                >
                  {t("login.privacy-policy")}
                </span>
                )}
              </div>
            </form>
          </div>
        </div>
        {getEnv("REACT_APP_VDC_PLATFORM") === "false" && domainLogo && (
          <img
            src={/^blob:/.test(domainLogo) ? domainLogo : ""}
            alt="Tenant Logo"
            className="logo"
            style={loginLogoStyle}
          />
        )}

        {/* <div className="ms-Grid-col ms-hiddenSm ms-md-2 ms-lg-3"></div> */}
      </div>
    </div>
  );
};

export default Login;
